import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { PackageDto, PackagingDto, PrinterDto, ScanResult } from '@thebb/shared';
import { get, patch, post } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, Modal, PageTitle, Spinner, fmtTime } from '../components/ui';

const kindLabel: Record<string, string> = { box: 'Kasse', envelope: 'Kuvert', pallet: 'Palle' };
const palletLabel: Record<string, string> = { full: 'Hel', half: 'Halv', quarter: 'Kvart' };
const kg = (g: number) => `${(g / 1000).toLocaleString('da-DK', { maximumFractionDigits: 1 })} kg`;
const dims = (p: { lengthMm: number | null; widthMm: number | null; heightMm: number | null }) => (p.lengthMm && p.widthMm && p.heightMm ? `${p.lengthMm / 10}×${p.widthMm / 10}×${p.heightMm / 10} cm` : '');

/** The packaging catalogue: boxes, envelopes, pallet types. */
export function PackagingPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [edit, setEdit] = useState<{ id: string | null; form: Record<string, string> } | null>(null);
  const list = useQuery({ queryKey: ['packaging', 'all'], queryFn: () => get<PackagingDto[]>('/packaging?includeInactive=true') });
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['packaging'] });
  const save = useMutation({
    mutationFn: (v: { id: string | null; form: Record<string, string> }) => {
      const n = (k: string) => (v.form[k] === '' || v.form[k] === undefined ? null : Number(v.form[k]));
      const body = { code: v.form.code, name: v.form.name, kind: v.form.kind, palletSize: v.form.kind === 'pallet' ? v.form.palletSize || 'full' : null, lengthMm: n('lengthCm') === null ? null : n('lengthCm')! * 10, widthMm: n('widthCm') === null ? null : n('widthCm')! * 10, heightMm: n('heightCm') === null ? null : n('heightCm')! * 10, tareWeightG: n('tareKg') === null ? 0 : Math.round(n('tareKg')! * 1000), maxWeightG: n('maxKg') === null ? null : Math.round(n('maxKg')! * 1000), sortOrder: n('sortOrder') ?? 100 };
      return v.id ? patch(`/packaging/${v.id}`, body) : post('/packaging', body);
    },
    onSuccess: () => { invalidate(); setEdit(null); },
    onError: setError,
  });
  const toggle = useMutation({ mutationFn: (p: PackagingDto) => patch(`/packaging/${p.id}`, { active: !p.active }), onSuccess: invalidate, onError: setError });
  const open = (p?: PackagingDto) =>
    setEdit({
      id: p?.id ?? null,
      form: p
        ? { code: p.code, name: p.name, kind: p.kind, palletSize: p.palletSize ?? '', lengthCm: p.lengthMm ? String(p.lengthMm / 10) : '', widthCm: p.widthMm ? String(p.widthMm / 10) : '', heightCm: p.heightMm ? String(p.heightMm / 10) : '', tareKg: String(p.tareWeightG / 1000), maxKg: p.maxWeightG ? String(p.maxWeightG / 1000) : '', sortOrder: String(p.sortOrder) }
        : { code: '', name: '', kind: 'box', palletSize: '', lengthCm: '', widthCm: '', heightCm: '', tareKg: '0', maxKg: '', sortOrder: '100' },
    });
  return (
    <div>
      <PageTitle title="Emballage" subtitle="Kasser, kuverter og palletyper til pakkestationen" actions={can('settings.manage') && <button className="btn btn-primary" onClick={() => open()}>Ny emballage</button>} />
      <ErrorBox error={error} />
      {list.isLoading ? (
        <Spinner />
      ) : (
        <table className="table card p-0">
          <thead><tr><th>Kode</th><th>Navn</th><th>Art</th><th>Mål</th><th className="text-right">Egenvægt</th><th className="text-right">Maks.</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {list.data?.map((p) => (
              <tr key={p.id} className={p.active ? '' : 'text-muted'}>
                <td className="font-medium">{p.code}</td>
                <td>{p.name}</td>
                <td>{kindLabel[p.kind]}{p.palletSize && ` · ${palletLabel[p.palletSize]}`}</td>
                <td>{dims(p)}</td>
                <td className="text-right tabular-nums">{kg(p.tareWeightG)}</td>
                <td className="text-right tabular-nums">{p.maxWeightG ? kg(p.maxWeightG) : ''}</td>
                <td>{p.active ? <Badge tone="green">aktiv</Badge> : <Badge>lukket</Badge>}</td>
                <td className="whitespace-nowrap text-right">{can('settings.manage') && <><button className="btn" onClick={() => open(p)}>Redigér</button>{' '}<button className="btn" onClick={() => toggle.mutate(p)}>{p.active ? 'Luk' : 'Åbn'}</button></>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {edit && (
        <Modal title={edit.id ? 'Redigér emballage' : 'Ny emballage'} onClose={() => setEdit(null)}>
          <div className="grid gap-3 md:grid-cols-3">
            <div><label className="label">Kode</label><input className="input" value={edit.form.code} onChange={(e) => setEdit({ ...edit, form: { ...edit.form, code: e.target.value } })} /></div>
            <div className="md:col-span-2"><label className="label">Navn</label><input className="input" value={edit.form.name} onChange={(e) => setEdit({ ...edit, form: { ...edit.form, name: e.target.value } })} /></div>
            <div><label className="label">Art</label><select className="input" value={edit.form.kind} onChange={(e) => setEdit({ ...edit, form: { ...edit.form, kind: e.target.value } })}><option value="box">Kasse</option><option value="envelope">Kuvert / pose</option><option value="pallet">Palle</option></select></div>
            {edit.form.kind === 'pallet' && <div><label className="label">Pallestørrelse</label><select className="input" value={edit.form.palletSize || 'full'} onChange={(e) => setEdit({ ...edit, form: { ...edit.form, palletSize: e.target.value } })}><option value="full">Hel</option><option value="half">Halv</option><option value="quarter">Kvart</option></select></div>}
            {[['lengthCm', 'Længde (cm)'], ['widthCm', 'Bredde (cm)'], ['heightCm', 'Højde (cm)'], ['tareKg', 'Egenvægt (kg)'], ['maxKg', 'Maks. vægt (kg)'], ['sortOrder', 'Sortering']].map(([k, l]) => (
              <div key={k}><label className="label">{l}</label><input className="input" type="number" min={0} step="0.1" value={edit.form[k!]} onChange={(e) => setEdit({ ...edit, form: { ...edit.form, [k!]: e.target.value } })} /></div>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-2"><button className="btn" onClick={() => setEdit(null)}>Annullér</button><button className="btn btn-primary" disabled={!edit.form.code || !edit.form.name || save.isPending} onClick={() => save.mutate(edit)}>Gem</button></div>
        </Modal>
      )}
    </div>
  );
}

/** Register parcels or pallets on a fulfilment or a batch. */
export function PackagesEditor({ ownerType, ownerId, packages, contentWeightG, pallets = false, disabled, onSaved, onError }: { ownerType: 'fulfilment' | 'batch'; ownerId: string; packages: PackageDto[]; contentWeightG?: number; pallets?: boolean; disabled?: boolean; onSaved: () => void; onError: (e: unknown) => void }) {
  const catalogue = useQuery({ queryKey: ['packaging'], queryFn: () => get<PackagingDto[]>('/packaging') });
  const options = (catalogue.data ?? []).filter((p) => (pallets ? p.kind === 'pallet' : p.kind !== 'pallet'));
  const [rows, setRows] = useState<{ packagingId: string; count: string; weightKg: string }[]>([]);
  useEffect(() => {
    if (!options.length) return;
    if (packages.length) {
      const grouped = new Map<string, { packagingId: string; count: number; weightKg: string }>();
      for (const p of packages) {
        const k = `${p.packagingId ?? ''}|${p.weightG}`;
        const g = grouped.get(k) ?? { packagingId: p.packagingId ?? '', count: 0, weightKg: String(p.weightG / 1000) };
        g.count++;
        grouped.set(k, g);
      }
      setRows([...grouped.values()].map((g) => ({ ...g, count: String(g.count) })));
    } else if (!rows.length) {
      setRows([{ packagingId: options[0]!.id, count: '1', weightKg: '' }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packages.length, options.length]);
  const save = useMutation({
    mutationFn: () => post(`/${ownerType === 'fulfilment' ? 'fulfilments' : 'batches'}/${ownerId}/packages`, { packages: rows.filter((r) => Number(r.count) > 0).map((r) => ({ packagingId: r.packagingId || null, count: Number(r.count), weightG: r.weightKg === '' ? null : Math.round(Number(r.weightKg) * 1000) })) }),
    onSuccess: onSaved,
    onError,
  });
  const total = packages.reduce((s, p) => s + p.weightG, 0);
  return (
    <div className="space-y-2 text-sm">
      {rows.map((r, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <input className="input w-16 text-right" type="number" min={1} value={r.count} disabled={disabled} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, count: e.target.value } : x)))} />
          <span>×</span>
          <select className="input w-56" value={r.packagingId} disabled={disabled} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, packagingId: e.target.value } : x)))}>
            {options.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input className="input w-28 text-right" type="number" min={0} step="0.1" placeholder="vægt kg" value={r.weightKg} disabled={disabled} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, weightKg: e.target.value } : x)))} title="Tom = varernes vægt fordelt + emballagens egenvægt" />
          {!disabled && rows.length > 1 && <button className="text-xs text-danger hover:underline" onClick={() => setRows(rows.filter((_, j) => j !== i))}>fjern</button>}
        </div>
      ))}
      {!disabled && (
        <div className="flex items-center gap-2">
          <button className="btn" onClick={() => setRows([...rows, { packagingId: options[0]?.id ?? '', count: '1', weightKg: '' }])}>{pallets ? '+ palle' : '+ kolli'}</button>
          <button className="btn btn-primary" disabled={save.isPending || !rows.length} onClick={() => save.mutate()}>Gem {pallets ? 'paller' : 'kolli'}</button>
          {contentWeightG !== undefined && <span className="text-xs text-muted">Varer vejer {kg(contentWeightG)}</span>}
        </div>
      )}
      {packages.length > 0 && <div className="text-xs text-muted">Registreret: {packages.length} {pallets ? 'paller' : 'kolli'}, i alt {kg(total)}</div>}
    </div>
  );
}

/** Scan to print: one field that takes an order, fulfilment or batch number and prints at the station. */
export function ScanPage() {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [last, setLast] = useState<ScanResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [printerId, setPrinterIdState] = useState(() => { try { return localStorage.getItem('station.laserPrinterId') ?? ''; } catch { return ''; } });
  const setPrinterId = (v: string) => { setPrinterIdState(v); try { localStorage.setItem('station.laserPrinterId', v); } catch { /* ignore */ } };
  const printers = useQuery({ queryKey: ['printers'], queryFn: () => get<PrinterDto[]>('/printing/printers') });
  const history = useQuery({ queryKey: ['scan-history'], queryFn: () => get<{ at: string; code: string; kind: string; number: string; printed: string[]; actorName: string }[]>('/scan/history'), refetchInterval: 15_000 });
  const input = useRef<HTMLInputElement>(null);
  const scan = useMutation({
    mutationFn: (c: string) => post<ScanResult>('/scan', { code: c, printerId: printerId || undefined }),
    onSuccess: (r) => {
      setLast(r);
      setError(null);
      setCode('');
      void qc.invalidateQueries({ queryKey: ['scan-history'] });
      if (!printerId && r.documentIds[0]) window.open(`/api/documents/${r.documentIds[0]}/download`, '_blank');
      input.current?.focus();
    },
    onError: (e) => { setError(e); setLast(null); setCode(''); input.current?.focus(); },
  });
  useEffect(() => input.current?.focus(), []);
  return (
    <div className="space-y-4">
      <PageTitle
        title="Scan og print"
        subtitle="Scan et ordrenummer, en ekspedition (971708-1) eller en batch (B-000001)"
        actions={
          <select className="input w-60" value={printerId} onChange={(e) => setPrinterId(e.target.value)}>
            <option value="">Ingen printer (åbn PDF)</option>
            {printers.data?.filter((p) => p.enabled && p.kind === 'pdf').map((p) => <option key={p.id} value={p.id}>{p.displayName}</option>)}
          </select>
        }
      />
      <form className="card flex items-center gap-3" onSubmit={(e) => { e.preventDefault(); if (code.trim()) scan.mutate(code.trim()); }}>
        <input ref={input} className="input h-14 text-2xl font-semibold tracking-wide" placeholder="Scan her…" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" />
        <button className="btn btn-primary h-14 px-6" disabled={!code.trim() || scan.isPending}>Print</button>
      </form>
      <ErrorBox error={error} />
      {last && (
        <div className="rounded-2xl border border-success/30 bg-success-100 px-5 py-4 text-success">
          <div className="text-lg font-semibold">{last.message}</div>
          <div className="text-sm">
            {last.kind === 'batch' ? <Link to="/batches/$id" params={{ id: last.id }} className="underline">Åbn batch {last.number}</Link> : <Link to="/pack/$id" params={{ id: last.id }} className="underline">Åbn {last.number} på pakkestationen</Link>} · status {last.status}
          </div>
        </div>
      )}
      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted">Seneste scanninger</h2>
        <table className="table card p-0">
          <thead><tr><th>Tid</th><th>Scannet</th><th>Fundet</th><th>Printet</th><th>Af</th></tr></thead>
          <tbody>
            {history.data?.map((h, i) => (
              <tr key={i}><td className="whitespace-nowrap">{fmtTime(h.at)}</td><td className="font-mono text-xs">{h.code}</td><td>{h.number}</td><td>{h.printed.join(', ') || <span className="text-muted">PDF</span>}</td><td>{h.actorName}</td></tr>
            ))}
            {history.data?.length === 0 && <tr><td colSpan={5} className="text-muted">Ingen scanninger endnu.</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}
